import { Button, cn, Input, Label, Spinner } from '@lensora/ui'
import * as React from 'react'

interface WebsiteTurnProps {
  resolved: boolean
  prefill: string
  submittedUrl?: string | undefined
  saving: boolean
  error: string | null
  onSubmit: (url: string, clientName?: string) => void
  autoFocusRef?: React.Ref<HTMLInputElement> | undefined
  canCreateClients: boolean
  clientNamePrefill: string
}

export function WebsiteTurn({
  resolved,
  prefill,
  submittedUrl,
  saving,
  error,
  onSubmit,
  autoFocusRef,
  canCreateClients,
  clientNamePrefill,
}: WebsiteTurnProps) {
  const [url, setUrl] = React.useState(prefill)
  const [ownerChoice, setOwnerChoice] = React.useState<'own' | 'client' | null>(null)
  const [clientName, setClientName] = React.useState(clientNamePrefill)

  if (resolved) {
    return <p className="text-sm text-text-muted">{submittedUrl}</p>
  }

  if (!canCreateClients) {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="onboarding-website-url">Website URL</Label>
          <Input
            id="onboarding-website-url"
            ref={autoFocusRef}
            type="url"
            placeholder="https://yourwebsite.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && url.trim() && !saving) onSubmit(url.trim())
            }}
          />
        </div>
        {error && <p className="text-sm text-feedback-error">{error}</p>}
        <Button size="sm" disabled={!url.trim() || saving} onClick={() => onSubmit(url.trim())}>
          {saving ? <><Spinner size="sm" className="mr-2" /> Adding…</> : 'Continue →'}
        </Button>
      </div>
    )
  }

  const isClient = ownerChoice === 'client'
  const canSubmit = url.trim().length > 0 && (!isClient || clientName.trim().length > 0)

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setOwnerChoice('own')}
          className={cn(
            'rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors',
            ownerChoice === 'own'
              ? 'border-brand-accent bg-brand-accent/10 text-brand-accent'
              : 'border-border text-text-muted hover:border-brand-accent/40 hover:text-text'
          )}
        >
          My own company
        </button>
        <button
          type="button"
          onClick={() => setOwnerChoice('client')}
          className={cn(
            'rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors',
            ownerChoice === 'client'
              ? 'border-brand-accent bg-brand-accent/10 text-brand-accent'
              : 'border-border text-text-muted hover:border-brand-accent/40 hover:text-text'
          )}
        >
          A client's
        </button>
      </div>
      {isClient && (
        <div className="space-y-1.5">
          <Label htmlFor="onboarding-client-name">Client name</Label>
          <Input
            id="onboarding-client-name"
            placeholder="Acme Corp"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
          />
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="onboarding-website-url">Website URL</Label>
        <Input
          id="onboarding-website-url"
          ref={autoFocusRef}
          type="url"
          placeholder="https://yourwebsite.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit && !saving) onSubmit(url.trim(), isClient ? clientName.trim() : undefined)
          }}
        />
      </div>
      {error && <p className="text-sm text-feedback-error">{error}</p>}
      <Button
        size="sm"
        disabled={!canSubmit || saving}
        onClick={() => onSubmit(url.trim(), isClient ? clientName.trim() : undefined)}
      >
        {saving ? <><Spinner size="sm" className="mr-2" /> Adding…</> : 'Continue →'}
      </Button>
    </div>
  )
}
